import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  QuestionnaireTemplateRegisterResponse,
  QuestionnaireTemplateRowWithVersions,
} from '@risksmart-app/trpc/src/types';
import { type GetQuestionnaireTemplatesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcQuestionnaireTemplateToGraphQL(
  questionnaireTemplate: QuestionnaireTemplateRowWithVersions
): GetQuestionnaireTemplatesQuery['questionnaire_template'][number] {
  return {
    ...questionnaireTemplate,
  };
}

/**
 * Maps TRPC third-party data to match the GraphQL query structure
 */
function mapTrpcQuestionnaireTemplatesToGraphQL(
  trpcData: QuestionnaireTemplateRegisterResponse | undefined
): GetQuestionnaireTemplatesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    questionnaire_template: trpcData.questionnaire_template.map(
      (questionnaireTemplate) =>
        mapTrpcQuestionnaireTemplateToGraphQL(questionnaireTemplate)
    ),
  };
}

export const useGetQuestionnaireTemplateRegisterTRPC = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.questionnaireTemplate.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcQuestionnaireTemplatesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
